import type React from "react";
import { fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Tile } from "../visuals/Tile";
import { DonutWithLegend, type DonutDatum } from "../visuals/DonutWithLegend";
import { TopBars, type TopBarItem } from "../visuals/TopBars";
import { Card, CardLabel } from "../visuals/Card";
import { ACCENT_BLUE, ACCENT_ORANGE } from "../colors";

const ALERT_COLORS = [ACCENT_ORANGE, ACCENT_BLUE, "#9B59B6", "#1ABC9C", "#E74C3C"];

function TeamAlertSlideView({ data, resetKey }: SlideContext): React.JSX.Element {
  const ta = data.team_alert;
  if (ta === null) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No team alert data uploaded for this period.
        </p>
      </div>
    );
  }

  const { total_alerts, by_store, by_type } = ta;

  const teamsWithAlerts = by_store.filter((s) => s.alerts > 0).length;
  const avgPerTeam = teamsWithAlerts > 0 ? total_alerts / teamsWithAlerts : 0;

  const donutData: DonutDatum[] = by_type
    .filter((t) => t.alerts > 0)
    .sort((a, b) => b.alerts - a.alerts)
    .map((t, i) => ({
      name: t.alert_type,
      value: t.alerts,
      color: ALERT_COLORS[i % ALERT_COLORS.length] ?? ACCENT_ORANGE,
    }));

  const topStores: TopBarItem[] = [...by_store]
    .sort((a, b) => b.alerts - a.alerts)
    .slice(0, 5)
    .map((s) => ({
      label: s.store,
      value: s.alerts,
      display: fmtInt(s.alerts),
    }));

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
        <Tile
          label="Total Team Alerts"
          value={total_alerts}
          format={(v) => fmtInt(Math.round(v))}
          delay={100}
          accent={ACCENT_ORANGE}
          resetKey={resetKey}
        />
        <Tile
          label="Teams with Alerts"
          value={teamsWithAlerts}
          format={(v) => fmtInt(Math.round(v))}
          delay={200}
          resetKey={resetKey}
        />
        <Tile
          label="Avg Alerts / Active Team"
          value={avgPerTeam}
          format={(v) => v.toFixed(1)}
          delay={300}
          resetKey={resetKey}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {donutData.length > 0 ? (
          <DonutWithLegend
            title="Alerts by Type"
            data={donutData}
            centerValue={total_alerts}
            centerLabel="alerts"
            delay={400}
            accentCenter={ACCENT_ORANGE}
          />
        ) : (
          <Card delay={400}>
            <CardLabel>Alerts by Type</CardLabel>
            <p className="text-white/40 text-sm font-body mt-6">
              Alert type breakdown not available.
            </p>
          </Card>
        )}
        <TopBars
          title="Top Teams by Alert Volume"
          items={topStores}
          delay={500}
          accentTop={ACCENT_ORANGE}
          accentRest={ACCENT_BLUE}
        />
      </div>
    </>
  );
}

export const teamAlertSlide: Slide = {
  id: "team_alert",
  title: "Team Alert Activity",
  render: (ctx) => <TeamAlertSlideView {...ctx} />,
};
